/**
 * Admin › Finanças › Ofertas de incentivo, the subsidised delivery fee (W10-DELIVERY-FOOD-LEVERS, D494, ADR-0111; lever L6) - the pure view-model behind the form where an
 * operator proposes a delivery-fee subsidy for a zone (or the whole market; OFF unless the market publishes `incentives.deliveryFeeSubsidyPolicy`), a DIFFERENT operator approves
 * it and either may end it early. The generic approve / end of the offers screen already serve every lever; this holds only what is the subsidy's own: the draft checks against the
 * BOUNDS the governed policy publishes (the server re-checks every one) and the body of a proposal.
 * Nothing here names a country, a currency or an amount.
 */
import type { ActiveIncentivesListView, ProposeActiveIncentiveInput } from '@vello/api-client';

export type DeliverySubsidyPolicyView = NonNullable<ActiveIncentivesListView['deliverySubsidyPolicy']>;

export type DeliverySubsidyDraftIssue = 'SHARE_REQUIRED' | 'SHARE_OVER_POLICY' | 'CAP_REQUIRED' | 'CAP_OVER_POLICY' | 'BUDGET_REQUIRED' | 'REASON_REQUIRED' | 'WINDOW_REQUIRED' | 'WINDOW_OVER_POLICY';

export interface DeliverySubsidyDraft {
  /** `MARKET` or a drawn zone's id. */
  readonly zoneId: string;
  /** The share of the delivery fee the platform pays, basis points; undefined until typed. */
  readonly subsidyBp: number | undefined;
  readonly perOrderCapMinor: bigint | undefined;
  readonly budgetMinor: bigint | undefined;
  readonly reasonCode: string;
  readonly windowHours: number | undefined;
}

export const EMPTY_DELIVERY_SUBSIDY_DRAFT: DeliverySubsidyDraft = { zoneId: 'MARKET', subsidyBp: undefined, perOrderCapMinor: undefined, budgetMinor: undefined, reasonCode: '', windowHours: undefined };

const minor = (m: { readonly amountMinor: string } | undefined): bigint | undefined => (m !== undefined && /^\d+$/.test(m.amountMinor) ? BigInt(m.amountMinor) : undefined);

/** The first thing wrong with a draft against the policy's published bounds, or undefined. */
export function deliverySubsidyDraftIssue(d: DeliverySubsidyDraft, policy: DeliverySubsidyPolicyView, reasons: readonly string[]): DeliverySubsidyDraftIssue | undefined {
  if (d.subsidyBp === undefined || d.subsidyBp < 1 || d.subsidyBp > 10_000) return 'SHARE_REQUIRED';
  if (d.subsidyBp > (policy.maxSubsidyBp ?? 0)) return 'SHARE_OVER_POLICY';
  if (d.perOrderCapMinor === undefined || d.perOrderCapMinor <= 0n) return 'CAP_REQUIRED';
  const max = minor(policy.maxSubsidyPerOrder);
  if (max !== undefined && d.perOrderCapMinor > max) return 'CAP_OVER_POLICY';
  if (d.budgetMinor === undefined || d.budgetMinor <= 0n) return 'BUDGET_REQUIRED';
  if (!reasons.includes(d.reasonCode)) return 'REASON_REQUIRED';
  if (d.windowHours === undefined) return 'WINDOW_REQUIRED';
  if (d.windowHours > (policy.maxWindowHours ?? 0)) return 'WINDOW_OVER_POLICY';
  return undefined;
}

/** The body of a proposal: an actor or a currency is never part of it (the session and the lever policy decide them). */
export function deliverySubsidyProposalBody(marketId: string, d: DeliverySubsidyDraft, nowIso: string): ProposeActiveIncentiveInput {
  const from = Date.parse(nowIso);
  return {
    marketId,
    lever: 'DELIVERY_FEE_SUBSIDY',
    ...(d.zoneId !== 'MARKET' ? { zoneId: d.zoneId } : {}),
    value: { subsidyBp: d.subsidyBp ?? 0, perOrderCapMinor: (d.perOrderCapMinor ?? 0n).toString() },
    spendCapMinor: (d.budgetMinor ?? 0n).toString(),
    reasonCode: d.reasonCode,
    validFrom: new Date(from).toISOString(),
    validTo: new Date(from + (d.windowHours ?? 0) * 3_600_000).toISOString(),
  };
}
